/**
 * Data layer for `campaign-tz-scheduling`. Thin typed fetch wrappers over /api/v1/campaign-tz-scheduling.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export type ScheduleScope = "offer" | "affiliate" | "global";
export type ScheduleAction = "allow" | "block" | "redirect_to";

export interface CampaignTzScheduleRow {
	id: string;
	tenant_id: string;
	campaign_id?: string | null;
	scope: ScheduleScope;
	scope_id?: string | null;
	timezone_iana: string;
	day_of_week_mask: number;
	hour_start: number;
	hour_end: number;
	action: ScheduleAction;
	target_url?: string | null;
	enabled: number;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListCampaignTzSchedulingResponse {
	count: number;
	items: CampaignTzScheduleRow[];
}

export interface CreateCampaignTzSchedulingBody {
	campaign_id?: string | null;
	scope: ScheduleScope;
	scope_id?: string | null;
	timezone_iana: string;
	day_of_week_mask: number;
	hour_start: number;
	hour_end: number;
	action: ScheduleAction;
	target_url?: string | null;
	enabled?: number;
	[key: string]: unknown;
}

const BASE = "/api/v1/campaign-tz-scheduling";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListCampaignTzSchedulingResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListCampaignTzSchedulingResponse>(res);
}

export async function create(
	body: CreateCampaignTzSchedulingBody,
): Promise<CampaignTzScheduleRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<CampaignTzScheduleRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const campaignTzSchedulingQueryKeys = {
	all: ["campaign-tz-scheduling"] as const,
	list: () => [...campaignTzSchedulingQueryKeys.all] as const,
	detail: (id: string) => [...campaignTzSchedulingQueryKeys.all, id] as const,
};
